import { useMemo } from "react"

import type { CompanyLocationAssociation, Location, ProductCompanyLocationAssociation } from "@/graphql/codegen/graphql"
import { useGetPurchaseOrderIncotermsLocations } from "@/screens/Procurement/PurchaseOrder/hooks/useGetPurchaseOrderIncotermsLocations"
import { sortIncotermsLocations } from "@/utils/locations"

interface UseMergedIncotermsLocationsReturn {
  locations: Location[]
  isFetchingLocations: boolean
  loadMoreLocations: () => void
}

export const useMergedIncotermsLocations = (
  companyLocationAssociations: (ProductCompanyLocationAssociation | CompanyLocationAssociation)[]
): UseMergedIncotermsLocationsReturn => {
  const {
    locations: incotermsLocations,
    isFetchingLocations,
    loadMoreLocations,
  } = useGetPurchaseOrderIncotermsLocations()

  const companyShippingLocations = useMemo(
    () =>
      companyLocationAssociations
        .flatMap((locationAssociation) => locationAssociation.shipping)
        .filter((location) => Boolean(location?.locationId)) as Location[],
    [companyLocationAssociations]
  )

  const mergedLocations = useMemo(() => {
    const locationIdSet = new Set<string>()
    const uniqueLocations = [...incotermsLocations, ...companyShippingLocations].filter((location) => {
      const isDuplicate = locationIdSet.has(location.locationId)
      locationIdSet.add(location.locationId)
      return !isDuplicate
    })
    return sortIncotermsLocations(uniqueLocations)
  }, [incotermsLocations, companyShippingLocations])

  return {
    locations: mergedLocations,
    isFetchingLocations,
    loadMoreLocations,
  }
}
